/**
 * DealChargesTab - Aba de cobranças financeiras no modal do deal
 * Lista cobranças vinculadas ao deal_id usando RPC paginada
 * Inclui botão "Nova Cobrança" com pré-preenchimento do deal e cliente
 * 
 * @version 4.0.0
 * RELEASE: 2026-03-16 v4.0.0 - Migrado para RPC get_cobrancas_paginated (unificação)
 * RELEASE: 2026-03-15 v3.0.0 - Botão Nova Cobrança com prefill deal+cliente
 * RELEASE: 2026-03-15 v2.0.0 - Refatorado para layout de tabela unificado
 * RELEASE: 2026-03-14 v1.0.0 - Criação da aba (Etapa 2)
 */
import React, { useState } from 'react';
import { useFinanceiroCobrancasPaginated } from '@/hooks/useFinanceiroCobrancasPaginated';
import { ChargesTableView } from '@/components/financial/ChargesTableView';
import { CreateChargeModal } from '@/components/financial/CreateChargeModal';
import { Button } from '@/components/ui/button';
import { Plus } from 'lucide-react';
// v4.2.0: trava do processo consultada antes do clique
import { useWorkflowGuardCheck } from '@/hooks/useWorkflowGuardCheck';
import { WorkflowGuardGate } from '@/components/workflow/WorkflowGuardGate';

/** Props do componente */
interface DealChargesTabProps {
  /** ID do deal */
  dealId: string;
  /** Se a aba está ativa (lazy loading) */
  isActive?: boolean;
  /** ID do cliente vinculado ao deal (para pré-preencher nova cobrança) */
  customerId?: string | null;
  /** v4.1.0: Permissão para criar nova cobrança (financial_charges.can_create) */
  canCreate?: boolean;
  /** v4.1.0: Permissão para editar cobrança (repassado a ChargesTableView -> ChargeDetailsModal) */
  canEdit?: boolean;
  /** v4.1.0: Permissão para excluir/cancelar cobrança */
  canDelete?: boolean;
}

/**
 * Aba de cobranças dentro do modal do deal
 * Usa RPC get_cobrancas_paginated com filtro deal_id (fonte de dados unificada)
 * Inclui botão para criar nova cobrança já vinculada ao negócio
 */
export function DealChargesTab({
  dealId,
  isActive = false,
  customerId,
  canCreate = true,
  canEdit = true,
  canDelete = true,
}: DealChargesTabProps) {
  // Estado do modal de criação de cobrança
  const [createChargeOpen, setCreateChargeOpen] = useState(false);

  // Busca cobranças do deal via RPC paginada (pageSize grande para listar todas)
  const { cobrancas, isLoading } = useFinanceiroCobrancasPaginated(
    isActive ? { deal_id: dealId } : undefined,
    { page: 1, pageSize: 200 }
  );

  // v4.2.0: política do processo para "Nova cobrança" (decisão real segue no banco)
  const guardNovaCobranca = useWorkflowGuardCheck({
    actionCode: 'financial.create_charge',
    rootEntityType: 'crm_deal',
    rootEntityId: dealId,
    enabled: Boolean(isActive && canCreate),
  });


  return (
    <div className="flex flex-col gap-3">
      {/* Cabeçalho com botão de ação — só renderiza se tem permissão de criar */}
      {canCreate && (
        <div className="flex items-center justify-end">
          <WorkflowGuardGate decisao={guardNovaCobranca}>
            <Button
              size="sm"
              onClick={() => setCreateChargeOpen(true)}
              className="h-8 text-xs gap-1.5"
              disabled={guardNovaCobranca.bloqueado}
            >
              <Plus className="h-3.5 w-3.5" />
              Nova Cobrança
            </Button>
          </WorkflowGuardGate>
        </div>
      )}

      {/* Tabela de cobranças (canEdit/canDelete repassados ao modal de detalhes) */}
      <ChargesTableView
        charges={cobrancas}
        isLoading={isLoading}
        emptyMessage="Nenhuma cobrança vinculada a este negócio"
        canEdit={canEdit}
        canDelete={canDelete}
        emptyAction={
          canCreate ? (
            <WorkflowGuardGate decisao={guardNovaCobranca}>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCreateChargeOpen(true)}
                className="text-xs gap-1.5"
                disabled={guardNovaCobranca.bloqueado}
              >
                <Plus className="h-3.5 w-3.5" />
                Criar primeira cobrança
              </Button>
            </WorkflowGuardGate>
          ) : undefined
        }
      />

      {/* Modal de criação com deal e cliente pré-preenchidos e travados */}
      {canCreate && (
        <CreateChargeModal
          open={createChargeOpen}
          onOpenChange={setCreateChargeOpen}
          prefillDealId={dealId}
          prefillCustomerId={customerId}
          lockDeal
        />
      )}
    </div>
  );
}
